import Vue from 'vue'
import { assert } from 'chai'
import _ from 'lodash'
import { apolloClient } from '~services/apollo'
import { authenticateMutation, listOrganizationsMutation, meQuery, currentOrganizationIdQuery, getOrganizationQuery } from '~src/graphql/auth.graphql'
import { formatBytes } from '~filters/filters'

/**
 * Processa as permissões do usuário a partir dos userProfiles retornados pelo meQuery.
 * Organiza as permissões em estrutura mais eficiente para consulta:
 * - org: permissões de nível organizacional (quando projectId é null)
 * - projects: permissões por projeto (indexadas por projectId)
 *
 * @param {Object} userProfiles - Objeto com nodes de UserProfile do GraphQL
 * @returns {Object} - { org: string[], projects: { [projectId]: string[] } }
 */
const processUserPermissions = userProfiles => {
  const permissions = {
    org: [],
    projects: {}
  }

  if (!userProfiles || !userProfiles.nodes) {
    return permissions
  }

  userProfiles.nodes.forEach(up => {
    const profilePermissions = up.profile?.profilePermissions?.nodes || []
    const perms = profilePermissions.map(pp => pp.permission?.key).filter(Boolean)

    if (up.projectId) {
      // Permissoes de projeto
      if (!permissions.projects[up.projectId]) {
        permissions.projects[up.projectId] = []
      }
      permissions.projects[up.projectId].push(...perms)
    } else {
      // Permissoes de organizacao
      permissions.org.push(...perms)
    }
  })

  // Remove duplicatas
  permissions.org = [...new Set(permissions.org)]
  Object.keys(permissions.projects).forEach(pid => {
    permissions.projects[pid] = [...new Set(permissions.projects[pid])]
  })

  return permissions
}

export default {
  async storeUserLoginDetails({ state, dispatch }) {
    const quota = await navigator.storage.estimate()
    const max = formatBytes(quota.quota, 2, 'MB')
    const used = formatBytes(quota.usage, 2, 'MB')
    const remaining = formatBytes(quota.quota - quota.usage, 2, 'MB')
    const userAgent = navigator.userAgent ?? ''
    dispatch('updateUserPreferences', {
      id: state.me.id,
      preferences: {
        ...state.me.preferences,
        lastLoginDetails: {
          date: new Date().toISOString(),
          userAgent,
          diskUsage: {
            max,
            used,
            remaining
          }
        }
      }
    })
  },
  getMe({ commit, state, dispatch }) {
    if (Vue.loggedUser) {
      commit('SET_ITEM', {
        item: Vue.loggedUser,
        state,
        modelName: 'me'
      })
    }
    return new Promise(resolve => {
      apolloClient
        .query({
          query: meQuery,
          fetchPolicy: 'no-cache'
        })
        .then(response => {
          const freshMe = response.data?.me

          if (!_.isEmpty(freshMe)) {
            dispatch('getCurrentOrganizationId').then(async currentOrganizationId => {
              const organizations =
                freshMe.userOrganizations?.nodes?.map(node => {
                  return node.organization
                }) || []

              const currentOrganization =
                organizations.find(organization => {
                  return organization?.id === currentOrganizationId
                }) || (await dispatch('getOrganizationById', currentOrganizationId))

              const currentUserOrganization = freshMe.userOrganizations?.nodes?.find(node => node.organization?.id === currentOrganizationId)

              Vue.setCurrentOrganization(currentOrganization)
              Vue.setOrganizations(organizations)

              // Mesclar dados frescos com dados cached para preservar campos enriquecidos
              const cachedMe = state.me || {}
              const me = {
                ...cachedMe, // Preserva campos como ui_customizations do cache
                ...freshMe, // Sobrescreve com dados frescos do GraphQL
                roles: [String(currentUserOrganization?.role || 'admin').toLowerCase()],
                permissions: processUserPermissions(freshMe.userProfiles)
              }

              // Commit para Vuex com dados mesclados
              commit('SET_ITEM', {
                item: me,
                state,
                modelName: 'me'
              })

              Vue.setLoggedUser(me)

              dispatch('storeUserLoginDetails')

              resolve(me)
            })
          }
        })
    })
  },

  getCurrentOrganizationId() {
    return apolloClient
      .query({
        query: currentOrganizationIdQuery,
        fetchPolicy: 'no-cache'
      })
      .then(response => {
        return response.data.currentOrganizationId
      })
  },
  getOrganizations({ dispatch }, user) {
    dispatch('startLoading', 'token:get')
    Vue.setToken('')
    assert.exists(user.username)
    assert.exists(user.password)
    return new Promise((resolve, reject) => {
      apolloClient
        .mutate({
          mutation: listOrganizationsMutation,
          // Parameters
          variables: {
            email: user.username,
            password: user.password
          }
        })
        .then(res => {
          const organizations = _.values(res.data.listOrganizations.results)
          Vue.setOrganizations(organizations)
          resolve(organizations)
        })
        .catch(err => {
          reject(err)
        })
        .finally(() => {
          dispatch('endLoading', 'token:get')
        })
    })
  },
  getToken({ dispatch }, payload) {
    Vue.setToken('')
    assert.exists(payload.username)
    assert.exists(payload.password)
    assert.exists(payload.organization)
    dispatch('startLoading', 'organization-token:get')
    return new Promise((resolve, reject) => {
      apolloClient
        .mutate({
          mutation: authenticateMutation,
          // Parameters
          variables: {
            email: payload.username,
            password: payload.password,
            organizationId: payload.organization
          }
        })
        .then(response => {
          dispatch('authViaToken', response.data.authenticate.jwtToken).then(() => {
            resolve()
          })
        })
        .catch(err => {
          reject(err)
        })
        .finally(() => {
          dispatch('endLoading', 'organization-token:get')
        })
    })
  },
  authViaToken({ dispatch }, token) {
    assert.exists(token)
    return new Promise(resolve => {
      Vue.setToken(token)

      dispatch('getMe').then(() => {
        Vue.bus.$emit('userLogin')
        resolve()
      })
    })
  },

  confirmRegister({ dispatch }) {
    dispatch('startLoading')
    // return new Promise((resolve, reject) => {
    //   UsersService.confirmRegister(token)
    //     .then(response => {
    //       Vue.setOrganizations(response.data.user.organizations)
    //       Vue.setCurrentOrganization(response.data.organization)
    //       Vue.setLoggedUser(response.data.user)
    //       Vue.setToken(response.data.organization_token)
    //       resolve(response.data.user)
    //     })
    //     .catch(response => {
    //       reject(response.response)
    //     })
    // }).finally(() => {
    //   dispatch('endLoading')
    // })
  },

  getOrganizationById(non, id) {
    if (window.navigator.onLine) {
      return apolloClient
        .query({
          query: getOrganizationQuery,
          variables: {
            id
          }
        })
        .then(response => {
          return response.data?.organization
        })
    } else {
      return new Promise(resolve => {
        resolve(Vue.currentOrganization)
      })
    }
  },

  changePasswordToken({ dispatch }) {
    dispatch('startLoading')
    // return new Promise((resolve, reject) => {
    //   UsersService.changePasswordToken(payload.email, payload.organizationId)
    //     .then(response => {
    //       resolve(response)
    //     })
    //     .catch(response => {
    //       reject(response.response)
    //     })
    // }).finally(() => {
    //   dispatch('endLoading')
    // })
  },
  changePassword({ dispatch }, payload) {
    assert.exists(payload.token)
    assert.exists(payload.password)

    dispatch('startLoading')
    // return new Promise((resolve, reject) => {
    //   UsersService.changePassword(payload.token, payload.password)
    //     .then(response => {
    //       resolve(response)
    //     })
    //     .catch(response => {
    //       reject(response.response)
    //     })
    // }).finally(() => {
    //   dispatch('endLoading')
    // })
  },
  acceptInvite({ dispatch }, payload) {
    assert.exists(payload.token)
    assert.exists(payload.name)
    assert.exists(payload.password)

    dispatch('startLoading')
    // return new Promise((resolve, reject) => {
    //   UsersService.acceptInvite(payload.token, payload.name, payload.password)
    //     .then(response => {
    //       resolve(response)
    //     })
    //     .catch(response => {
    //       reject(response.response)
    //     })
    // }).finally(() => {
    //   dispatch('endLoading')
    // })
  }
}
